// エラーメッセージ翻訳マップ
const ERROR_MESSAGES: Record<string | number, string> = {
  // HTTP ステータスコード
  400: 'リクエストが無効です',
  401: '認証が必要です',
  403: 'アクセス権限がありません',
  404: 'データが見つかりません',
  409: 'データが重複しています',
  422: '入力データに問題があります',
  500: 'サーバーエラーが発生しました',
  502: 'サーバーに接続できません',
  503: 'サービスが一時的に利用できません',

  // 一般的なエラーメッセージ
  'network error': 'ネットワークエラーが発生しました',
  timeout: 'タイムアウトしました',
  'failed to fetch': 'サーバーとの通信に失敗しました',
  'connection refused': 'サーバーに接続できません',

  // 認証関連
  'invalid credentials': 'ユーザーIDまたはパスワードが間違っています',
  'authentication failed': '認証に失敗しました',
  'session expired': 'セッションが期限切れです',
  unauthorized: '認証が必要です',
  forbidden: 'アクセス権限がありません',

  // 顧客管理関連
  'customer not found': '顧客が見つかりません',
  'customer already exists': '顧客が既に存在します',
  'invalid phone number': '電話番号の形式が正しくありません',
  'duplicate phone number': 'この電話番号は既に登録されています',
  'customer name required': '顧客名を入力してください',
  'phone number required': '電話番号を入力してください',

  // 預金・利用関連
  'insufficient balance': '残高が不足しています',
  'invalid amount': '金額が正しくありません',
  'amount required': '金額を入力してください',
  'negative amount': '金額は正の値を入力してください',
  'deposit not found': '預金記録が見つかりません',
  'usage not found': '利用記録が見つかりません',

  // 店舗関連
  'store not found': '店舗が見つかりません',
  'store id required': '店舗IDが必要です',
  'invalid store': '無効な店舗です',

  // ユーザー関連
  'user not found': 'ユーザーが見つかりません',
  'invalid user id': 'ユーザーIDが正しくありません',
  'invalid password': 'パスワードが正しくありません',
  'user id required': 'ユーザーIDを入力してください',
  'password required': 'パスワードを入力してください',

  // 旧エラーメッセージの互換性
  'staff not found': 'ユーザーが見つかりません',
  'invalid staff id': 'ユーザーIDが正しくありません',
  'staff id required': 'ユーザーIDを入力してください',

  // データベース関連
  'database error': 'データベースエラーが発生しました',
  'constraint violation': 'データの整合性エラーが発生しました',
  'foreign key violation': '参照データが存在しません',
  'unique constraint violation': '重複するデータがあります',

  // バリデーション関連
  'validation error': '入力データに問題があります',
  'required field missing': '必須項目が入力されていません',
  'invalid format': '形式が正しくありません',
  'value too long': '値が長すぎます',
  'value too short': '値が短すぎます',
  'invalid date': '日付が正しくありません',
  'future date not allowed': '未来の日付は入力できません',

  // システム関連
  'internal server error': 'サーバー内部エラーが発生しました',
  'service unavailable': 'サービスが一時的に利用できません',
  'maintenance mode': 'メンテナンス中です',
  'rate limit exceeded': 'リクエスト数の上限を超えました',

  // その他
  'unknown error': '不明なエラーが発生しました',
  'operation failed': '操作に失敗しました',
  'invalid request': '無効なリクエストです',
  'bad request': 'リクエストが正しくありません'
};

interface ApiErrorResponse {
  status?: number;
  error?: string;
  message?: string;
  detail?: string;
  success?: boolean;
}

/**
 * エラーメッセージを日本語に翻訳する関数
 * @param error - エラーオブジェクト、エラーメッセージ、またはAPIレスポンス
 * @param defaultMessage - デフォルトメッセージ（翻訳が見つからない場合）
 * @returns 日本語のエラーメッセージ
 */
export const translateErrorMessage = (
  error: string | Error | ApiErrorResponse | unknown,
  defaultMessage: string = 'エラーが発生しました'
): string => {
  // エラーが空またはnullの場合
  if (!error) {
    return defaultMessage;
  }

  if (typeof error === 'object' && (error as ApiErrorResponse).success === true) {
    return (error as ApiErrorResponse).message || '処理が完了しました';
  }

  // 文字列の場合
  if (typeof error === 'string') {
    const normalizedError = error.toLowerCase().trim();

    // 直接マッピングを探す
    if (ERROR_MESSAGES[normalizedError]) {
      return ERROR_MESSAGES[normalizedError];
    }

    // 部分マッチを探す
    for (const [key, value] of Object.entries(ERROR_MESSAGES)) {
      if (normalizedError.includes(key.toString().toLowerCase())) {
        return value;
      }
    }

    // HTTP status codeを含む場合を処理
    const statusMatch = normalizedError.match(/status:\s*(\d+)/);
    if (statusMatch) {
      const statusCode = parseInt(statusMatch[1]);
      if (ERROR_MESSAGES[statusCode]) {
        return ERROR_MESSAGES[statusCode];
      }
    }

    // 既に日本語の場合はそのまま返す
    if (/[\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FAF]/.test(error)) {
      return error;
    }

    return defaultMessage;
  }

  // Errorオブジェクトの場合
  if (error instanceof Error) {
    return translateErrorMessage(error.message, defaultMessage);
  }

  // APIレスポンスオブジェクトの場合
  if (typeof error === 'object' && error !== null) {
    const apiError = error as ApiErrorResponse;

    // HTTPレスポンスオブジェクトの場合
    if (apiError.status) {
      const statusMessage = ERROR_MESSAGES[apiError.status];
      if (statusMessage) {
        return statusMessage;
      }
    }

    // errorプロパティがある場合
    if (apiError.error) {
      return translateErrorMessage(apiError.error, defaultMessage);
    }

    // messageプロパティがある場合
    if (apiError.message) {
      return translateErrorMessage(apiError.message, defaultMessage);
    }

    // detailプロパティがある場合（FastAPI形式）
    if (apiError.detail) {
      return translateErrorMessage(apiError.detail, defaultMessage);
    }
  }

  return defaultMessage;
};

/**
 * 特定のエラーメッセージを追加する関数
 * @param key - エラーキー
 * @param message - 日本語メッセージ
 */
export const addErrorMessage = (key: string | number, message: string): void => {
  ERROR_MESSAGES[typeof key === 'string' ? key.toLowerCase() : key] = message;
};

/**
 * 利用可能なエラーメッセージ一覧を取得する関数
 * @returns エラーメッセージマップ
 */
export const getErrorMessages = (): Record<string | number, string> => {
  return { ...ERROR_MESSAGES };
};

export default translateErrorMessage;
